// src/pages/EditListing.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getListingById, updateListing } from '../services/ListingsService';
import { geocodeAddress } from '../services/GeocodingService';
import { uploadMultipleMedia, deleteMultipleMedia } from '../services/StorageService';
import MultiMediaUpload from '../components/MultiMediaUpload';
import LocationAutocomplete from '../components/LocationAutocomplete';
import './Listings.css';

function EditListing() {
  const { id } = useParams();
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    isVirtual: false,
    price: '',
    isFreeEvent: false,
    isContactForPrice: false,
    media: [],
    imageUrl: '', // Keep for backward compatibility
    contact: '',
    coordinates: null,
    maxAttendees: '',
    locations: [createEmptyLocation()],
    // Event dates (optional)
    eventStart: '',
    eventEnd: '',
    // Social media fields
    socialMedia: {
      website: '',
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: ''
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geocodingError, setGeocodingError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [originalMedia, setOriginalMedia] = useState([]);
  const [currentMediaPreviews, setCurrentMediaPreviews] = useState([]);
  const [locationErrors, setLocationErrors] = useState({});
  const MAX_LOCATIONS = 5;
  const createEmptyLocation = () => ({
    id: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    address: '',
    coordinates: null
  });
  
  // Fetch the listing data on component mount
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const data = await getListingById(id);
        
        // Check if user is authenticated and is the owner of this listing
        if (!currentUser) {
          setError("Please log in to edit this listing.");
          setLoading(false);
          return;
        }
        
        if (data.userId !== currentUser.uid && !isAdmin) {
          setError("You don't have permission to edit this listing.");
          setLoading(false);
          return;
        }
        
        // Get the price value
        const price = data.price || '0';
        const priceValue = parseFloat(price);
        
        // Determine flags for backward compatibility
        let isFreeEvent = Boolean(data.isFreeEvent);
        let isContactForPrice = Boolean(data.isContactForPrice);
        
        // For backward compatibility with legacy $0 listings that don't have flags
        if (priceValue === 0 && !isFreeEvent && !isContactForPrice) {
          isFreeEvent = true; // Default $0 to Free Event
        }
        
        // Convert legacy imageUrl to media array if needed
        let media = data.media || [];
        if (!media.length && data.imageUrl) {
          media = [{ url: data.imageUrl, type: 'image' }];
        }
        
        // Helper to format Date -> datetime-local string
        const toInputDT = (d) => {
          if (!d) return '';
          const pad = (n) => String(n).padStart(2, '0');
          const yyyy = d.getFullYear();
          const mm = pad(d.getMonth() + 1);
          const dd = pad(d.getDate());
          const hh = pad(d.getHours());
          const mi = pad(d.getMinutes());
          return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
        };

        // Set form data from the fetched listing
        setFormData({
          title: data.title || '',
          description: data.description || '',
          category: data.category || '',
          location: data.location || '',
          isVirtual: data.isVirtual || false,
          price: price,
          isFreeEvent: isFreeEvent,
          isContactForPrice: isContactForPrice,
          media: media,
          imageUrl: data.imageUrl || '',
          contact: data.contact || '',
          coordinates: data.coordinates || null,
          maxAttendees: data.maxAttendees ? data.maxAttendees.toString() : '',
          eventStart: data.eventStart ? toInputDT(new Date(data.eventStart)) : '',
          eventEnd: data.eventEnd ? toInputDT(new Date(data.eventEnd)) : '',
          locations: (
            Array.isArray(data.locations) && data.locations.length > 0
              ? data.locations.map((loc, index) => ({
                  id: loc.id || `${data.id || id}-loc-${index}`,
                  address: loc.address || '',
                  coordinates: loc.coordinates || null
                }))
              : [{
                  id: `${data.id || id}-loc-primary`,
                  address: data.location || '',
                  coordinates: data.coordinates || null
                }]
          ),
          socialMedia: data.socialMedia || {
            website: '',
            facebook: '',
            instagram: '',
            twitter: '',
            linkedin: '',
            youtube: ''
          }
        });
        
        // Save original media for comparison later
        setOriginalMedia(media);
        setCurrentMediaPreviews(media);
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching listing:", err);
        setError("Failed to load listing details. Please try again later.");
        setLoading(false);
      }
    };
    
    // Only fetch if we have a current user
    if (currentUser) {
      fetchListing();
    } else {
      setError("Please log in to edit this listing.");
      setLoading(false);
    }
  }, [id, currentUser]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle social media fields
    if (name.startsWith('social_')) {
      const platform = name.replace('social_', '');
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [platform]: value
        }
      }));
      return;
    }
    
    // Handle checkbox inputs
    if (type === 'checkbox') {
      setFormData(prev => {
        const next = {
          ...prev,
          [name]: checked,
          ...(name === 'isVirtual' && checked ? { coordinates: null } : {})
        };
        if (name === 'isVirtual' && !checked && prev.locations?.length) {
          next.location = prev.locations[0].address || '';
          next.coordinates = prev.locations[0].coordinates || null;
        }
        return next;
      });
      
      // If isVirtual is being checked, reset location errors
      if (name === 'isVirtual' && checked) {
        setGeocodingError(null);
        setLocationErrors({});
      }
      
      // If isFreeEvent is checked, set price to 0 and uncheck isContactForPrice
      if (name === 'isFreeEvent' && checked) {
        setFormData(prev => ({
          ...prev,
          price: '0',
          isContactForPrice: false
        }));
      }
      
      // If isContactForPrice is checked, set price to 0 and uncheck isFreeEvent
      if (name === 'isContactForPrice' && checked) {
        setFormData(prev => ({
          ...prev,
          price: '0',
          isFreeEvent: false
        }));
      }
      
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If price is changed and not 0, uncheck both price-related checkboxes
    if (name === 'price' && value !== '0' && value !== '') {
      setFormData(prev => ({
        ...prev,
        isFreeEvent: false,
        isContactForPrice: false
      }));
    }
  };
  
  const updateLocationsState = (updater) => {
    setFormData(prev => {
      const nextLocations = updater(prev.locations || []);
      const ensuredLocations = nextLocations.length > 0 ? nextLocations : [createEmptyLocation()];
      const primary = ensuredLocations[0];
      return {
        ...prev,
        locations: ensuredLocations,
        location: prev.isVirtual ? prev.location : (primary.address || ''),
        coordinates: prev.isVirtual ? null : (primary.coordinates || null)
      };
    });
  };
  
  const handleLocationInputChange = (locationId, value) => {
    updateLocationsState(prevLocations =>
      prevLocations.map(loc =>
        loc.id === locationId ? { ...loc, address: value, coordinates: null } : loc
      )
    );
    setLocationErrors(prev => ({ ...prev, [locationId]: '' }));
    setGeocodingError(null);
  };
  
  const handleLocationSelect = (locationId, suggestion) => {
    updateLocationsState(prevLocations =>
      prevLocations.map(loc =>
        loc.id === locationId
          ? {
              ...loc,
              address: suggestion.displayName,
              coordinates: { lat: suggestion.lat, lng: suggestion.lng }
            }
          : loc
      )
    );
    setLocationErrors(prev => ({ ...prev, [locationId]: '' }));
    setGeocodingError(null);
  };
  
  const handleAddLocation = () => {
    updateLocationsState(prevLocations => {
      if (prevLocations.length >= MAX_LOCATIONS) return prevLocations;
      return [...prevLocations, createEmptyLocation()];
    });
  };
  
  const handleRemoveLocation = (locationId) => {
    updateLocationsState(prevLocations => {
      if (prevLocations.length === 1) return prevLocations;
      return prevLocations.filter(loc => loc.id !== locationId);
    });
    setLocationErrors(prev => {
      const next = { ...prev };
      delete next[locationId];
      return next;
    });
  };
  
  const handleMediaSelected = (files, previews) => {
    setMediaFiles(files);
    setCurrentMediaPreviews(previews || []);
    setUploadError(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setGeocodingError(null);
    setUploadError(null);
    
    try {
      // Process coordinates and multiple locations
      let coordinates = null;
      let resolvedLocations = formData.locations || [];
      setLocationErrors({});
      
      if (!formData.isVirtual) {
        const nextErrors = {};
        const cleanedLocations = [];
        
        for (let i = 0; i < resolvedLocations.length; i++) {
          const entry = resolvedLocations[i];
          const trimmedAddress = (entry.address || '').trim();
          if (!trimmedAddress) {
            if (i === 0) {
              nextErrors[entry.id] = 'Primary location is required.';
            }
            continue;
          }
          
          let entryCoordinates = entry.coordinates;
          if (
            entryCoordinates &&
            typeof entryCoordinates.lat === 'number' &&
            typeof entryCoordinates.lng === 'number'
          ) {
            // keep existing coordinates
          } else {
            try {
              entryCoordinates = await geocodeAddress(trimmedAddress);
            } catch (geoError) {
              console.error("Geocoding error:", geoError);
              nextErrors[entry.id] = "Couldn't verify this address. Please try another.";
            }
          }
          
          cleanedLocations.push({
            ...entry,
            address: trimmedAddress,
            coordinates: entryCoordinates || null
          });
          
          if (!coordinates && entryCoordinates) {
            coordinates = entryCoordinates;
          }
        }
        
        if (cleanedLocations.length === 0) {
          nextErrors[
            resolvedLocations[0]?.id || 'primary'
          ] = 'Add at least one location.';
        }
        
        if (Object.keys(nextErrors).length > 0) {
          setLocationErrors(nextErrors);
          setGeocodingError("Please fix the highlighted locations before saving.");
          setIsSubmitting(false);
          return;
        }
        
        resolvedLocations = cleanedLocations;
        if (!coordinates && resolvedLocations[0]?.coordinates) {
          coordinates = resolvedLocations[0].coordinates;
        }
      } else {
        resolvedLocations = [];
      }
      
      if (formData.isVirtual) {
        coordinates = null;
      }
      setGeocodingError(null);
      
      // Handle media changes (uploads, removals, reordering)
      let updatedMedia = currentMediaPreviews;
      
      // If there are new files to upload
      if (mediaFiles.length > 0) {
        try {
          // Upload new media files
          const newMedia = await uploadMultipleMedia(mediaFiles, id);
          
          if (newMedia.length > 0) {
            // Add new media to existing media that wasn't removed
            updatedMedia = [...currentMediaPreviews.filter(m => !m.file), ...newMedia];
          }
        } catch (uploadErr) {
          console.error("Media upload error:", uploadErr);
          setUploadError(uploadErr.message || "Failed to upload media. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }
      
      // Handle media deletions - compare current previews with original media
      const mediaToDelete = originalMedia
        .filter(originalItem => 
          originalItem.url && 
          originalItem.url.includes('firebase') &&
          !currentMediaPreviews.some(currentItem => currentItem.url === originalItem.url)
        )
        .map(m => m.url);
      
      if (mediaToDelete.length > 0) {
        try {
          await deleteMultipleMedia(mediaToDelete);
        } catch (deleteErr) {
          console.error("Error deleting old media (non-critical):", deleteErr);
          // Continue even if delete fails
        }
      }
      
      // Prepare event dates if provided
      let eventStartDate = null;
      let eventEndDate = null;
      if (formData.eventStart) {
        eventStartDate = new Date(formData.eventStart);
      }
      if (formData.eventEnd) {
        eventEndDate = new Date(formData.eventEnd);
        if (eventStartDate && eventEndDate < eventStartDate) {
          alert('Event end must be after the start.');
          setIsSubmitting(false);
          return;
        }
      }

      const { eventStart: _omitStart, eventEnd: _omitEnd, locations: _omitLocations, ...restForm } = formData;
      const legacyLocation = formData.isVirtual
        ? formData.location
        : resolvedLocations[0]?.address || formData.location;
      await updateListing(id, {
        ...restForm,
        category: formData.category || 'other',
        location: legacyLocation,
        locations: resolvedLocations,
        coordinates,
        media: updatedMedia,
        maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees) : null,
        ...(eventStartDate ? { eventStart: eventStartDate } : {}),
        ...(eventEndDate ? { eventEnd: eventEndDate } : {}),
        // Keep imageUrl for backward compatibility (use first image if available)
        imageUrl: updatedMedia.find(m => m.type === 'image')?.url || ''
      });
      
      setIsSubmitting(false);
      alert("Listing updated successfully!");
      navigate(`/listings/${id}`);
    } catch (error) {
      console.error("Error updating listing:", error);
      setError("Failed to update listing. Please try again.");
      setIsSubmitting(false);
    }
  };
  
  if (loading) return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>;
  if (error) return <div style={{ color: 'red', textAlign: 'center', padding: '2rem' }}>{error}</div>;
  
  return (
    <div style={{ maxWidth: '600px', margin: '2rem auto', padding: '0 1rem' }}>
      <h1 style={{ color: 'var(--color-primary)' }}>Edit Listing</h1>
      <p>Update your listing information.</p>
      
      <form onSubmit={handleSubmit} style={{ marginTop: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="title" style={{ display: 'block', marginBottom: '0.5rem' }}>Title*</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="description" style={{ display: 'block', marginBottom: '0.5rem' }}>Description*</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows="6"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', whiteSpace: 'pre-wrap' }}
            placeholder="Describe your listing. Line breaks and spacing will be preserved in the display."
          ></textarea>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="category" style={{ display: 'block', marginBottom: '0.5rem' }}>Category (Optional)</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="">Select a category</option>
            <option value="education">Education</option>
            <option value="recreation">Recreation</option>
            <option value="childcare">Childcare</option>
            <option value="mentorship">Mentorship</option>
            <option value="professional_development">Professional Development</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              id="isVirtual"
              name="isVirtual"
              checked={formData.isVirtual}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isVirtual">This is a virtual listing</label>
          </div>
        </div>
        
        {!formData.isVirtual && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Locations*</label>
            <p style={{ marginTop: 0, marginBottom: '0.75rem', color: '#666', fontSize: '0.9rem' }}>
              Update one or more physical locations for this listing.
            </p>
            {formData.locations.map((loc, index) => (
              <div 
                key={loc.id} 
                style={{ 
                  marginBottom: '0.75rem', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px', 
                  padding: '0.75rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                  <strong>Location {index + 1}</strong>
                  {index === 0 && <span style={{ fontSize: '0.8rem', color: '#999' }}>Primary</span>}
                </div>
                <LocationAutocomplete
                  id={`location-${loc.id}`}
                  name={`location-${loc.id}`}
                  value={loc.address}
                  onChange={(value) => handleLocationInputChange(loc.id, value)}
                  onSelect={(suggestion) => handleLocationSelect(loc.id, suggestion)}
                  required={index === 0}
                  disabled={isSubmitting}
                  placeholder="Enter a full address (123 Main St, City, Province/State, Country)"
                />
                {locationErrors[loc.id] && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#e74c3c' }}>
                    {locationErrors[loc.id]}
                  </small>
                )}
                {!locationErrors[loc.id] && loc.address && !loc.coordinates && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#666' }}>
                    Select a suggestion to lock in this address.
                  </small>
                )}
                {!locationErrors[loc.id] && loc.coordinates && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#2ecc71' }}>
                    Coordinates saved ({loc.coordinates.lat.toFixed(4)}, {loc.coordinates.lng.toFixed(4)}).
                  </small>
                )}
                {formData.locations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLocation(loc.id)}
                    style={{
                      marginTop: '0.5rem',
                      background: 'transparent',
                      border: 'none',
                      color: '#c0392b',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {formData.locations.length < MAX_LOCATIONS && (
              <button
                type="button"
                onClick={handleAddLocation}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  backgroundColor: '#f1f5f9',
                  border: '1px dashed #b0bec5',
                  borderRadius: '4px',
                  padding: '0.4rem 0.75rem',
                  cursor: 'pointer'
                }}
              >
                + Add Another Location
              </button>
            )}
            {geocodingError && (
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#e74c3c' }}>
                {geocodingError}
              </small>
            )}
          </div>
        )}

        {/* Event Date/Time */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>When does the event occur?</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label htmlFor="eventStart" style={{ display: 'block', marginBottom: '0.25rem' }}>Start</label>
              <input
                type="datetime-local"
                id="eventStart"
                name="eventStart"
                value={formData.eventStart}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label htmlFor="eventEnd" style={{ display: 'block', marginBottom: '0.25rem' }}>End (optional)</label>
              <input
                type="datetime-local"
                id="eventEnd"
                name="eventEnd"
                value={formData.eventEnd}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, eventStart: '', eventEnd: '' }))}
              style={{
                background: 'transparent',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '0.35rem 0.75rem',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Clear Dates
            </button>
          </div>
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
            Adding the event date helps it show correctly on calendars.
          </small>
        </div>

        {formData.isVirtual && (
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="location" style={{ display: 'block', marginBottom: '0.5rem' }}>Virtual Location Details</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Zoom, Google Meet, Online Platform"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
        )}
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>Price Options*</label>
          
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              id="isFreeEvent"
              name="isFreeEvent"
              checked={formData.isFreeEvent}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isFreeEvent">This is a free event</label>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              id="isContactForPrice"
              name="isContactForPrice"
              checked={formData.isContactForPrice}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isContactForPrice">Contact for pricing</label>
          </div>
          
          {!formData.isFreeEvent && !formData.isContactForPrice && (
            <div>
              <label htmlFor="price" style={{ display: 'block', marginBottom: '0.5rem' }}>Price ($)</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                required={!formData.isFreeEvent && !formData.isContactForPrice}
                placeholder="0.00"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
          )}
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>Upload Media (Images/Videos)</label>
          <MultiMediaUpload 
            onMediaSelected={handleMediaSelected}
            initialMedia={formData.media}
          />
          {uploadError && (
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#e74c3c' }}>
              {uploadError}
            </small>
          )}
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
            Upload new files to add or replace media. Remove unwanted files before uploading new ones.
          </small>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="maxAttendees" style={{ display: 'block', marginBottom: '0.5rem' }}>Maximum Attendees (Optional)</label>
          <input
            type="number"
            id="maxAttendees"
            name="maxAttendees"
            value={formData.maxAttendees}
            onChange={handleChange}
            min="1"
            placeholder="Leave empty for unlimited"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
            Leave empty if there's no attendance limit.
          </small>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="contact" style={{ display: 'block', marginBottom: '0.5rem' }}>Contact Email*</label>
          <input
            type="email"
            id="contact"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        
        {/* Social Media Section */}
        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>Social Media & Website (Optional)</h3>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_website" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Website</label>
            <input
              type="url"
              id="social_website"
              name="social_website"
              value={formData.socialMedia.website}
              onChange={handleChange}
              placeholder="https://www.example.com"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_facebook" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Facebook</label>
            <input
              type="url"
              id="social_facebook"
              name="social_facebook"
              value={formData.socialMedia.facebook}
              onChange={handleChange}
              placeholder="https://www.facebook.com/yourpage"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_instagram" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Instagram</label>
            <input
              type="text"
              id="social_instagram"
              name="social_instagram"
              value={formData.socialMedia.instagram}
              onChange={handleChange}
              placeholder="@yourusername or https://www.instagram.com/yourusername"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_twitter" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Twitter/X</label>
            <input
              type="text"
              id="social_twitter"
              name="social_twitter"
              value={formData.socialMedia.twitter}
              onChange={handleChange}
              placeholder="@yourusername or https://twitter.com/yourusername"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_linkedin" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>LinkedIn</label>
            <input
              type="url"
              id="social_linkedin"
              name="social_linkedin"
              value={formData.socialMedia.linkedin}
              onChange={handleChange}
              placeholder="https://www.linkedin.com/in/yourprofile"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_youtube" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>YouTube</label>
            <input
              type="url"
              id="social_youtube"
              name="social_youtube"
              value={formData.socialMedia.youtube}
              onChange={handleChange}
              placeholder="https://www.youtube.com/channel/yourchannel"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
        </div>
        
        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>* Required fields</p>
        
        <button 
          type="submit" 
          disabled={isSubmitting}
          style={{ 
            background: 'var(--color-primary)', 
            color: 'white', 
            border: 'none', 
            padding: '0.75rem 1.5rem', 
            borderRadius: '4px', 
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            marginTop: '1rem',
            opacity: isSubmitting ? 0.7 : 1
          }}
        >
          {isSubmitting ? 'Updating...' : 'Update Listing'}
        </button>
      </form>
    </div>
  );
}

export default EditListing;
